<template>
  <div class="task-list-container">
    <div class="search-wrapper">
      <span class="d-none d-md-inline">{{ $t('pages.project.show.search') }}</span>
      <proa-select
        v-model="filterUserId"
        :options="filterUserOptions"
        class="search-select"
      />
    </div>
    <draggable
      class="task-list-wrapper"
      :draggable="isOwner ? '.task-list' : ''"
      handle=".task-list-header"
      v-model="taskLists"
      v-bind="dragOptions"
      @end="onDragEndTaskList"
    >
      <div
        v-for="taskList of taskLists"
        :key="taskList.id"
        class="task-list"
      >
        <div :class="['task-list-header', { 'draggable': isOwner }]">
          <span class="task-list-title">
            <span>{{ taskList.title }}</span>
            <span v-if="taskCards[taskList.id]">（{{ taskCards[taskList.id].length }}）</span>
          </span>
          <div
            v-if="isOwner"
            class="task-list-item"
            v-click-outside="() => { if (displayingTaskListMenuWithId != taskList.id) return; displayingTaskListMenuWithId = null }"
            @click="displayingTaskListMenuWithId = taskList.id"
          >
            <img :src="require('@/assets/images/icons/ellipsis_wh.png')" />
            <div
              v-show="displayingTaskListMenuWithId == taskList.id"
              class="task-list-menu"
            >
              <div
                @click.stop="editTaskListModalWithObject = taskList; displayingTaskListMenuWithId = null"
                class="menu"
              >
                {{ $t('pages.project.show.editTaskList') }}
              </div>
              <div
                @click.stop="deleteTaskListModalWithId = taskList.id; displayingTaskListMenuWithId = null"
                class="menu"
              >
                {{ $t('pages.project.show.deleteTaskList') }}
              </div>
            </div>
          </div>
        </div>
        <div class="task-list-body-wrapper">
          <div class="task-list-body">
            <div class="add-task-button-wrapper">
              <proa-button
                variant="light-gray"
                @click.native="$router.push({ name: 'newProjectTaskCards', params: { id: project.id, taskListId: taskList.id } })"
              >
                {{ $t('common.addTask') }}
              </proa-button>
            </div>
            <draggable
              group="taskCards"
              class="task-cards"
              handle=".move-handler"
              v-model="taskCards[taskList.id]"
              v-bind="dragOptions"
              :sort="false"
              :move="onDragMoveTaskCard"
              @end="onDragEndTaskCard"
            >
              <task-card-card
                v-for="taskCard of taskCards[taskList.id]"
                :key="taskCard.id"
                :taskCard="taskCard"
                @click.native="$router.push({ name: 'showProjectTaskCards', params: { projectId: project.id, id: taskCard.id } })"
              />
            </draggable>
          </div>
        </div>
      </div>
      <proa-button
        v-if="isOwner"
        class="add-list-button default-button-width"
        @click.native="addTaskListModal = true"
      >
        {{ $t('common.addList') }}
      </proa-button>
    </draggable>
    <add-task-list-modal
      :modal="addTaskListModal"
      :requesting="createTaskListRequesting"
      @close="closeAddTaskListModal"
      @onClickCreateTaskList="onClickCreateTaskList"
    />
    <edit-task-list-modal
      :modal="editTaskListModalWithObject != null"
      :editTaskList="editTaskListModalWithObject"
      :requesting="updateTaskListRequesting"
      @close="closeEditTaskListModal"
      @onClickUpdateTaskList="onClickUpdateTaskList"
    />
    <delete-task-list-modal
      :modal="deleteTaskListModalWithId != null"
      :requesting="deleteTaskListRequesting"
      :deleteTaskList="deleteTaskList"
      @close="closeDeleteTaskListModal"
      @onConfirmedDeleteTaskList="onConfirmedDeleteTaskList"
    />
  </div>
</template>

<script>
import i18n from '@/i18n'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import ClickOutside from 'vue-click-outside'
import draggable from 'vuedraggable'
import Project from '@/store/models/Project'
import TaskList from '@/store/models/TaskList'
import TaskCard from '@/store/models/TaskCard'
import User from '@/store/models/User'

import AddTaskListModal from '@/components/molecules/projectMenuTab/AddTaskListModal'
import EditTaskListModal from '@/components/molecules/projectMenuTab/EditTaskListModal'
import DeleteTaskListModal from '@/components/molecules/projectMenuTab/DeleteTaskListModal'
import TaskCardCard from '@/components/molecules/projectMenuTab/TaskCardCard'

export default {
  name: 'ProjectMenuTabTaskLists',
  directives: {
    ClickOutside
  },
  components: {
    draggable,
    AddTaskListModal,
    EditTaskListModal,
    DeleteTaskListModal,
    TaskCardCard
  },
  computed: {
    filterUserOptions: function() {
      return [{
        label: i18n.tc('common.allMember'),
        value: null
      }].concat(this.projectUsers.map((user) => {
        return {
          label: user.meta.displayName,
          value: user.id
        }
      }))
    },
    projectId: function() { return this.$route.params.id },
    project: function() { return Project.find(this.projectId) },
    projectUsers: function() { return User.findIn(this.project.userIds) },
    isOwner: function() { return this.project.projectOwnerId == this.signedInUser.id },
    dragOptions: () => {
      return {
        animation: 200
      }
    },
    deleteTaskList: function() { return TaskList.find(this.deleteTaskListModalWithId) },
    ...mapGetters({
      signedInUser: 'auth/signedInUser',
      taskCardSyncRequest: 'entities/taskCards/syncRequest'
    })
  },
  data() {
    return {
      filterUserId: null,
      taskLists: [],
      taskCards: {},
      addTaskListModal: false,
      displayingTaskListMenuWithId: null,
      draggingTaskCardId: null,
      editTaskListModalWithObject: null,
      deleteTaskListModalWithId: null,
      createTaskListRequesting: false,
      updateTaskListRequesting: false,
      deleteTaskListRequesting: false
    }
  },
  methods: {
    closeAddTaskListModal: function() {
      if (this.createTaskListRequesting) return
      this.addTaskListModal = false
    },
    closeEditTaskListModal: function() {
      if (this.updateTaskListRequesting) return
      this.editTaskListModalWithObject = null
    },
    closeDeleteTaskListModal: function() {
      if (this.deleteTaskListRequesting) return
      this.deleteTaskListModalWithId = null
    },
    setInitResources: function() {
      this.setTaskCardSyncRequest(false)
      this.setTaskLists()
      this.setTaskCards()
    },
    setTaskLists: function() {
      this.taskLists = TaskList.query().where((taskList) => {
        return taskList.projectId == this.projectId && taskList.listType != 'archive'
      }).orderBy('listIndex', 'asc').get()
    },
    setTaskCards: function() {
      this.taskCards = this.taskLists.map(taskList => taskList.id).reduce((result, current) => {
        result[current] = TaskCard.query().where((taskCard) => {
          return taskCard.taskListId == current && (!this.filterUserId || taskCard.userIds.includes(Number(this.filterUserId)))
        }).orderBy('startAt', 'asc').get()
        return result
      }, {})
    },
    onDragMoveTaskCard: function(e) {
      this.draggingTaskCardId = e.draggedContext.element.id
    },
    async onDragEndTaskList() {
      const taskListsAttributes = {
        taskListsAttributes: this.taskLists.map((taskList, listIndex) => {
          return {
            id: taskList.id,
            listIndex: listIndex
          }
        })
      }
      await this.apiPutProject({
        projectId: this.projectId,
        attributes: taskListsAttributes
      })
    },
    async onDragEndTaskCard() {
      let updateTaskListId
      Object.keys(this.taskCards).forEach(taskListId => {
        this.taskCards[taskListId].forEach(taskCard => {
          if (taskCard.id == this.draggingTaskCardId) {
            updateTaskListId = taskListId
          }
        })
      })
      if (updateTaskListId) {
        await this.apiPutTaskCard({
          projectId: this.projectId,
          id: this.draggingTaskCardId,
          attributes: {
            taskListId: updateTaskListId
          }
        })
        this.setTaskCards()
      }
    },
    async onClickCreateTaskList(newTaskList) {
      this.createTaskListRequesting = true
      await this.apiPostTaskList({
        projectId: this.projectId,
        attributes: newTaskList
      })
      this.createTaskListRequesting = false
      this.setTaskLists()
      this.setTaskCards()
      this.closeAddTaskListModal()
    },
    async onClickUpdateTaskList(editTaskList) {
      this.updateTaskListRequesting = true
      await this.apiPutTaskList({
        projectId: this.projectId,
        id: this.editTaskListModalWithObject.id,
        attributes: editTaskList
      })
      this.updateTaskListRequesting = false
      this.setTaskLists()
      this.setTaskCards()
      this.closeEditTaskListModal()
    },
    async onConfirmedDeleteTaskList() {
      this.deleteTaskListRequesting = true
      await this.apiDeleteTaskList({
        projectId: this.projectId,
        id: this.deleteTaskListModalWithId
      })
      this.deleteTaskListRequesting = false
      this.setTaskLists()
      this.setTaskCards()
      this.closeDeleteTaskListModal()
    },
    ...mapMutations({
      setTaskCardSyncRequest: 'entities/taskCards/syncRequest'
    }),
    ...mapActions({
      apiPutProject: 'entities/projects/update',
      apiPostTaskList: 'entities/taskLists/create',
      apiPutTaskList: 'entities/taskLists/update',
      apiDeleteTaskList: 'entities/taskLists/destroy',
      apiPutTaskCard: 'entities/taskCards/update'
    })
  },
  async created() {
    this.setInitResources()
  },
  watch: {
    filterUserId: function() {
      this.setTaskCards()
    },
    taskCardSyncRequest: function(value) {
      if (value) {
        this.setTaskCards()
        this.setTaskCardSyncRequest(false)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/mediaquery";
@import "@/styles/variables";

.task-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-wrapper {
  font-size: 13px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;

  .search-select {
    max-width: 160px;

    @include mq(md) {
      margin-left: 15px;
    }
  }
}

.task-list-wrapper {
  flex: 1;
  display: flex;
  align-items: flex-start;
  overflow: auto;

  .task-list {
    flex: 0 0 280px;
    height: 100%;
    margin-right: 10px;
    display: flex;
    flex-direction: column;
  }

  .task-list-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    width: 280px;
    height: 40px;
    background-color: $secondary-color;
    color: white;
    border-radius: 4px;
    padding: 10px 15px;
    font-size: 13px;
    font-weight: 600;
    z-index: 2;

    .task-list-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex-basis: 80%;
    }

    &.draggable {
      cursor: move;
    }

    .task-list-item {
      cursor: pointer;

      img {
        width: 20px;
        height: 20px;
      }
    }

    .task-list-menu {
      position: absolute;
      z-index: 2;
      top: 35px;
      right: 0;
      width: 150px;
      background-color: white;
      border-radius: 4px;
      box-shadow: 0px 0px 4px #00000026;
      color: $secondary-color;

      .menu {
        cursor: pointer;
        padding: 10px;
        font-size: 13px;
        font-weight: 300;
        border-radius: 4px;

        &:hover {
          background-color: $light-gray-color;
        }
      }
    }
  }

  .task-list-body-wrapper {
    flex-grow: 1;
    height: 100%;
    position: relative;

    .task-list-body {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow: auto;
      background-color: $light-gray-color;
      padding: 10px 15px;
      border-radius: 4px;

      .add-task-button-wrapper {
        text-align: right;
      }

      .task-cards {
        flex-grow: 1;
        padding-top: 10px;
      }
    }
  }

  .add-list-button {
    width: 150px;
    flex-shrink: 0;
  }
}
</style>
